import { useEffect, useState, type FormEvent } from "react";
import { SyntaxCode } from "@/office/SyntaxCode";
import { cloudFile, cloudFind, cloudTree, type CloudHit, type CloudTree } from "@/system/cloud";
import type { LocateQuality } from "@/system/locate";

type CloudPanelProps = {
  /** 时间轴当前步骤命中的文件（云端路径）与行号 */
  activePath: string | null;
  activeLine: number | null;
  activeStepId: number | null;
  /** 当前模块关键词，用于搜索结果排序 */
  hint: string;
  /** 这一步为什么定位到这里 */
  basis?: string;
  quality?: LocateQuality;
};

type OpenedFile = { path: string; code: string; lines: number };

export function CloudPanel(props: CloudPanelProps) {
  const [tree, setTree] = useState<CloudTree | null>(null);
  const [dir, setDir] = useState(".");
  const [treeError, setTreeError] = useState("");
  const [opened, setOpened] = useState<OpenedFile | null>(null);
  const [fileError, setFileError] = useState("");
  const [hitLine, setHitLine] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CloudHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [showBrowser, setShowBrowser] = useState(true);

  const loadTree = async (nextDir: string) => {
    setTreeError("");
    try {
      const data = await cloudTree(nextDir);
      setTree(data);
      setDir(data.dir || ".");
    } catch (error) {
      setTreeError(error instanceof Error ? error.message : "目录读取失败");
    }
  };

  const openFile = async (path: string, line: number | null) => {
    setFileError("");
    setHitLine(line);
    try {
      const data = await cloudFile(path);
      setOpened({ path: data.path, code: data.code, lines: data.lines });
      if (line && line > data.lines) setHitLine(null);
    } catch (error) {
      setFileError(error instanceof Error ? error.message : "文件读取失败");
    }
  };

  useEffect(() => {
    void loadTree(".");
  }, []);

  // 时间轴切到某一步时，云端命中的文件直接打开
  useEffect(() => {
    if (!props.activePath) return;
    void openFile(props.activePath, props.activeLine);
  }, [props.activePath, props.activeLine, props.activeStepId]);

  const submitSearch = async (event: FormEvent) => {
    event.preventDefault();
    const text = query.trim();
    if (text.length < 2) return;
    setSearching(true);
    try {
      const hits = await cloudFind(text, props.hint, 8);
      setResults(hits);
      if (hits.length > 0) await openFile(hits[0].path, hits[0].line);
    } catch (error) {
      setFileError(error instanceof Error ? error.message : "检索失败");
    } finally {
      setSearching(false);
    }
  };

  const parentDir = dir.includes("/") ? dir.slice(0, dir.lastIndexOf("/")) : ".";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {props.basis ? (
        <div className="shrink-0 border-b border-[#16222e] bg-[#101b25] px-3 py-1.5">
          <p className="flex items-start gap-2 text-[11px] leading-4 text-[#8fa8bd]">
            <span
              className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] ${
                props.quality === "exact"
                  ? "bg-[#123528] text-[#5fd0a5]"
                  : props.quality === "header"
                    ? "bg-[#33280f] text-[#d4a24a]"
                    : "bg-[#22201a] text-[#c9c2b3]"
              }`}
            >
              {props.quality === "exact"
                ? "原文命中"
                : props.quality === "header"
                  ? "按表头定位"
                  : props.quality === "fallback"
                    ? "兜底"
                    : "未找到"}
            </span>
            <span className="min-w-0 flex-1">{props.basis}</span>
          </p>
        </div>
      ) : null}

      <form onSubmit={submitSearch} className="flex shrink-0 items-center gap-2 border-b border-[#16222e] px-3 py-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="在云端仓库里搜代码或页面文案，例如 导出、工资表"
          className="h-8 min-w-0 flex-1 rounded border border-[#22323f] bg-[#0a1118] px-2.5 font-mono text-[11.5px] text-[#cfe3f5] outline-none placeholder:text-[#4e6478] focus:border-[#3a5a74]"
        />
        <button
          type="submit"
          disabled={searching}
          className="h-8 shrink-0 rounded border border-[#24384a] px-2.5 text-[11.5px] text-[#9db3c7] transition-colors hover:border-[#3a5a74] hover:text-[#cfe3f5] disabled:opacity-50"
        >
          {searching ? "搜索中…" : "搜索"}
        </button>
        <button
          type="button"
          onClick={() => setShowBrowser((value) => !value)}
          className="h-8 shrink-0 rounded border border-[#24384a] px-2.5 text-[11.5px] text-[#9db3c7] transition-colors hover:border-[#3a5a74] hover:text-[#cfe3f5]"
        >
          {showBrowser ? "收起文件" : "浏览文件"}
        </button>
      </form>

      {results ? (
        <div className="shrink-0 border-b border-[#16222e] bg-[#0a1118] px-3 py-1.5">
          <div className="flex items-center gap-2 text-[10.5px] text-[#6e8599]">
            <span>命中 {results.length} 个文件</span>
            <button type="button" onClick={() => setResults(null)} className="ml-auto hover:text-[#cfe3f5]">
              清空
            </button>
          </div>
          <div className="mt-1 flex max-h-[76px] flex-col gap-0.5 overflow-auto">
            {results.map((hit) => (
              <button
                key={`${hit.path}-${hit.line}`}
                type="button"
                onClick={() => void openFile(hit.path, hit.line)}
                className={`flex items-center gap-2 rounded px-1 py-0.5 text-left font-mono text-[10.5px] transition-colors ${
                  opened?.path === hit.path ? "bg-[#17242f] text-[#cfe3f5]" : "text-[#7c93a8] hover:bg-[#121c26]"
                }`}
              >
                <span className="shrink-0 text-[#5fd0c2]">L{hit.line}</span>
                <span className="min-w-0 truncate">{hit.path}</span>
                <span className="ml-auto hidden min-w-0 max-w-[45%] truncate text-[#4e6478] lg:block">{hit.text}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {showBrowser ? (
        <div className="shrink-0 border-b border-[#16222e] bg-[#0b141c] px-3 py-2">
          <div className="flex items-center gap-2 font-mono text-[10.5px] text-[#6e8599]">
            <button type="button" onClick={() => void loadTree(".")} className="hover:text-[#cfe3f5]">
              根目录
            </button>
            {dir !== "." ? (
              <>
                <span className="text-[#2b3d4d]">/</span>
                <span className="min-w-0 truncate text-[#9db3c7]">{dir}</span>
                <button type="button" onClick={() => void loadTree(parentDir)} className="ml-1 hover:text-[#cfe3f5]">
                  上一层
                </button>
              </>
            ) : null}
          </div>
          {treeError ? <p className="mt-1 text-[11px] text-[#c0503a]">{treeError}</p> : null}
          <div className="mt-1 flex max-h-[92px] flex-wrap gap-1 overflow-auto">
            {(tree?.dirs ?? []).map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => void loadTree(dir === "." ? name : `${dir}/${name}`)}
                className="rounded border border-[#22323f] px-1.5 py-0.5 font-mono text-[10.5px] text-[#8fa8bd] transition-colors hover:border-[#3a5a74] hover:text-[#cfe3f5]"
              >
                {name}/
              </button>
            ))}
            {(tree?.files ?? []).map((file) => (
              <button
                key={file.path}
                type="button"
                onClick={() => void openFile(file.path, null)}
                className={`rounded px-1.5 py-0.5 font-mono text-[10.5px] transition-colors ${
                  opened?.path === file.path
                    ? "bg-[#17242f] text-[#cfe3f5]"
                    : "text-[#69809a] hover:bg-[#121c26] hover:text-[#a9bdcd]"
                }`}
              >
                {file.path.split("/").pop()}
              </button>
            ))}
            {tree && tree.dirs.length === 0 && tree.files.length === 0 ? (
              <span className="font-mono text-[10.5px] text-[#4e6478]">这个目录里没有可读的代码文件</span>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-3 py-2">
        {fileError ? <p className="mb-2 text-[11px] text-[#c0503a]">{fileError}</p> : null}
        {opened ? (
          <div className="overflow-hidden rounded border border-[#1a2836]">
            <div className="flex items-center gap-2 border-b border-[#1a2836] bg-[#101b25] px-3 py-1.5">
              <span className="min-w-0 truncate font-mono text-[11px] text-[#7fb3ff]">{opened.path}</span>
              <span className="ml-auto shrink-0 font-mono text-[10.5px] text-[#4e6478]">
                {hitLine ? `第 ${hitLine} 行` : "完整文件"} · {opened.lines} 行
              </span>
            </div>
            <SyntaxCode
              code={opened.code}
              highlightRange={hitLine ? { start: Math.max(1, hitLine - 1), end: hitLine + 1 } : undefined}
            />
          </div>
        ) : (
          <p className="px-1 py-6 font-mono text-[11.5px] leading-6 text-[#4e6478]">
            左侧点一个按钮，或在上面搜索一段文案，云端仓库里的对应代码会显示在这里。
          </p>
        )}
      </div>
    </div>
  );
}
